import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Timeline as TimelineIcon,
  ColorLens as ColorLensIcon,
  Straighten as StraightenIcon
} from '@mui/icons-material';
import { caboService, Cabo, CaboFormData, CaboPonto, CABO_TIPOS, CABO_FUNCOES_ESTRUTURAIS, CABO_PADROES_TUBO_LOOSE, CABO_QTDE_FO_OPCOES, CABO_QTDE_FO_OPCOES_ASU, PadraoTuboLoose } from '../../services/caboService';
import { getOpcoesQtdeTuboLoose, sugerirQtdeTuboLoose, validarCaboTuboLoose } from '../../utils/tuboLooseUtils';
import { FtthCaixa } from '../../services/ftthCaixaService';
import { FtthArmario } from '../../services/ftthArmarioService';
import EscritorioSelect from '../selects/EscritorioSelect';

type CaboPontoForm = {
  lat: number;
  lng: number;
  id?: number;
  caixa_armario_id?: number | null;
  caixa_armario_tipo?: 'caixa' | 'armario' | null;
};

interface CabosFormProps {
  cabo?: Cabo;
  pontos: CaboPontoForm[];
  caixas?: FtthCaixa[];
  armarios?: FtthArmario[];
  onSuccess: (cabo: Cabo) => void;
  onCancel: () => void;
  /** z-index dos menus dos selects (use em dialogs aninhados, ex.: diagrama FTTH) */
  menuZIndex?: number;
}

function mergePontosComVinculo(pontosMapa: CaboPontoForm[], pontosCabo?: CaboPonto[]): CaboPontoForm[] {
  return pontosMapa.map((ponto, index) => {
    const pontoBackend = pontosCabo?.find((p) => ponto.id != null && p.id === ponto.id) ?? pontosCabo?.[index];
    return {
      ...ponto,
      id: ponto.id ?? pontoBackend?.id,
      caixa_armario_id: ponto.caixa_armario_id ?? pontoBackend?.caixa_armario_id ?? null,
      caixa_armario_tipo: ponto.caixa_armario_tipo ?? pontoBackend?.caixa_armario_tipo ?? null,
    };
  });
}

function getPontoVinculoLabel(
  ponto: CaboPontoForm,
  caixas: FtthCaixa[],
  armarios: FtthArmario[],
): string | null {
  if (!ponto.caixa_armario_id || !ponto.caixa_armario_tipo) {
    return null;
  }

  if (ponto.caixa_armario_tipo === 'caixa') {
    const caixa = caixas.find((item) => item.id === ponto.caixa_armario_id);
    if (caixa) {
      if (caixa.tipo === 'reserva') {
        const metragem = caixa.metragem;
        return metragem != null && Number.isFinite(Number(metragem))
          ? `Reserva: ${metragem} m`
          : `Reserva #${caixa.id}`;
      }
      const tipoLabel = caixa.tipo === 'ceo' ? 'CEO' : 'CTO';
      return `${tipoLabel}: ${caixa.name}`;
    }
    return `Caixa #${ponto.caixa_armario_id}`;
  }

  const armario = armarios.find((item) => item.id === ponto.caixa_armario_id);
  return armario ? `Armário: ${armario.name}` : `Armário #${ponto.caixa_armario_id}`;
}

const CabosForm: React.FC<CabosFormProps> = ({
  cabo,
  pontos,
  caixas = [],
  armarios = [],
  onSuccess,
  onCancel,
  menuZIndex,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CaboFormData>({
    titulo: '',
    pontos: [],
    qtde_fo: 12,
    qtde_tubo_loose: 1,
    color: '#424242',
    tipo: 'as',
    padrao_tubo_loose: 'abnt' as PadraoTuboLoose,
    funcao_estrutural: 'distribuicao',
    escritorio_id: 1,
    fabricante: '',
    lote: '',
    observacoes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectFormControlSx = menuZIndex != null
    ? { '& .MuiPopover-root': { zIndex: menuZIndex } }
    : undefined;
  const selectMenuProps = menuZIndex != null
    ? { MenuProps: { disablePortal: true, sx: { zIndex: menuZIndex } } }
    : {};

  const predefinedColors = [
    { color: '#424242', name: 'Cinza' },
    { color: '#000000', name: 'Preto' },
    { color: '#2196F3', name: 'Azul' },
    { color: '#4CAF50', name: 'Verde' },
    { color: '#FF9800', name: 'Laranja' },
    { color: '#F44336', name: 'Vermelho' }
  ];

  useEffect(() => {
    console.log('🔄 CabosForm: useEffect triggered');
    console.log('📋 cabo prop:', cabo);
    console.log('📍 pontos prop:', pontos);
    console.log('📍 pontos length:', pontos.length);

    if (cabo) {
      console.log('✏️ CabosForm: Modo EDIÇÃO - usando pontos do mapa e dados do cabo original');
      setFormData({
        titulo: cabo.titulo,
        pontos: mergePontosComVinculo(pontos, cabo.pontos),
        qtde_fo: cabo.qtde_fo,
        qtde_tubo_loose: cabo.qtde_tubo_loose ?? 1,
        color: cabo.color,
        tipo: cabo.tipo || 'as',
        padrao_tubo_loose: cabo.padrao_tubo_loose || 'abnt',
        funcao_estrutural: cabo.funcao_estrutural || 'distribuicao',
        escritorio_id: cabo.escritorio_id || 1,
        fabricante: cabo.fabricante || '',
        lote: cabo.lote || '',
        observacoes: cabo.observacoes || '',
      });
    } else {
      console.log('➕ CabosForm: Modo CRIAÇÃO - usando apenas pontos do mapa');
      setFormData(prev => ({
        ...prev,
        pontos: pontos
      }));
    }

    console.log('✅ CabosForm: formData atualizado');
  }, [cabo, pontos]);

  // Debug: Monitorar mudanças no formData
  useEffect(() => {
    console.log('📊 CabosForm: formData ALTERADO:');
    console.log('📍 Pontos no formData:', formData.pontos.length);
    console.log('📋 Título:', formData.titulo);
    console.log('🎨 Cor:', formData.color);
    console.log('🔢 Qtde FO:', formData.qtde_fo);
  }, [formData]);

  const opcoesFo = useMemo(
    () => (formData.tipo === 'asu' ? [...CABO_QTDE_FO_OPCOES_ASU] : [...CABO_QTDE_FO_OPCOES]),
    [formData.tipo],
  );

  const opcoesTubosLoose = useMemo(
    () => getOpcoesQtdeTuboLoose(formData.qtde_fo),
    [formData.qtde_fo],
  );

  const fibrasPorTubo = useMemo(() => {
    const tubos = formData.tipo === 'asu' ? 1 : (formData.qtde_tubo_loose ?? 1);
    return tubos > 0 ? Math.floor(formData.qtde_fo / tubos) : formData.qtde_fo;
  }, [formData.qtde_fo, formData.qtde_tubo_loose, formData.tipo]);

  const handleInputChange = (field: keyof CaboFormData, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };

      if (field === 'tipo' && value === 'asu') {
        next.padrao_tubo_loose = 'abnt';
        next.qtde_tubo_loose = 1;
        if (!CABO_QTDE_FO_OPCOES_ASU.includes(next.qtde_fo as typeof CABO_QTDE_FO_OPCOES_ASU[number])) {
          next.qtde_fo = 12;
        }
      }

      if (field === 'qtde_fo') {
        const fo = Number(value);
        next.qtde_fo = fo;
        const sugerido = sugerirQtdeTuboLoose(fo, next.tipo);
        const opcoes = getOpcoesQtdeTuboLoose(fo);
        next.qtde_tubo_loose = opcoes.includes(next.qtde_tubo_loose ?? 1)
          ? (next.qtde_tubo_loose ?? 1)
          : sugerido;
        if (next.tipo === 'asu') next.qtde_tubo_loose = 1;
      }

      return next;
    });

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'Título é obrigatório';
    }

    if (!formData.escritorio_id) {
      newErrors.escritorio_id = 'Escritório é obrigatório';
    }

    if (!formData.qtde_fo || formData.qtde_fo < 1) {
      newErrors.qtde_fo = 'Quantidade deve ser no mínimo 1';
    }

    const tuboError = validarCaboTuboLoose(
      formData.qtde_fo,
      formData.qtde_tubo_loose ?? 1,
      formData.tipo,
      formData.padrao_tubo_loose,
    );
    if (tuboError) {
      newErrors.qtde_fo = tuboError;
    }

    if (!formData.qtde_tubo_loose || formData.qtde_tubo_loose < 1) {
      newErrors.qtde_tubo_loose = 'Informe a quantidade de tubos loose';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo do cabo é obrigatório';
    }

    if (!formData.funcao_estrutural) {
      newErrors.funcao_estrutural = 'Função estrutural é obrigatória';
    }

    const pontosError = caboService.validatePontos(formData.pontos);
    if (pontosError) {
      newErrors.pontos = pontosError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      console.log('📤 CabosForm: Iniciando salvamento do cabo...');
      console.log('📊 CabosForm: Dados do formulário:', formData);
      console.log('📍 CabosForm: Quantidade de pontos sendo enviados:', formData.pontos.length);
      console.log('📍 CabosForm: Pontos detalhados:', formData.pontos);

      let result: Cabo;

      if (cabo) {
        console.log('✏️ CabosForm: Atualizando metadados do cabo...');
        // Atualizar apenas metadados do cabo (pontos já salvos individualmente no mapa)
        result = await caboService.updateCabo(cabo.id, {
          titulo: formData.titulo,
          qtde_fo: formData.qtde_fo,
          color: formData.color,
          tipo: formData.tipo,
          padrao_tubo_loose: formData.tipo === 'asu' ? 'abnt' : formData.padrao_tubo_loose,
          qtde_tubo_loose: formData.tipo === 'asu' ? 1 : (formData.qtde_tubo_loose ?? 1),
          funcao_estrutural: formData.funcao_estrutural,
          escritorio_id: formData.escritorio_id,
          fabricante: formData.fabricante?.trim() || null,
          lote: formData.lote?.trim() || null,
          observacoes: formData.observacoes?.trim() || null,
        });
        // Buscar pontos atuais do BD para retornar ao mapa com IDs corretos
        const pontosAtualizados = await caboService.getPontos(cabo.id);
        result = { ...result, pontos: pontosAtualizados };
      } else {
        console.log('➕ CabosForm: Criando novo cabo...');
        result = await caboService.createCabo(formData);
      }

      console.log('✅ CabosForm: Cabo salvo com sucesso:', result);
      console.log('📞 CabosForm: Chamando onSuccess...');

      // Chamar onSuccess e aguardar caso seja uma função async
      await onSuccess(result);

      console.log('🎉 CabosForm: onSuccess executado com sucesso!');
    } catch (error: any) {
      console.error('❌ CabosForm: Erro ao salvar cabo:', error);
      setErrors({ submit: error.message || 'Erro ao salvar cabo' });
    } finally {
      setLoading(false);
    }
  };

  const distance = caboService.calculateDistance(formData.pontos);
  const formattedDistance = caboService.formatDistance(distance);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {cabo ? 'Editar Cabo' : 'Cadastrar Novo Cabo'}
      </Typography>

      {errors.submit && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.submit}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              📋 Informações do Cabo
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  size="small"
                  label="Título do Cabo"
                  value={formData.titulo}
                  onChange={(e) => handleInputChange('titulo', e.target.value)}
                  error={!!errors.titulo}
                  helperText={errors.titulo || 'Nome identificador do cabo'}
                  placeholder="Ex: Cabo Principal - Centro"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <EscritorioSelect
                  value={formData.escritorio_id || 1}
                  onChange={(value) => handleInputChange('escritorio_id', Number(value) || 1)}
                  fullWidth
                  size="small"
                  required
                  label="Escritório"
                  placeholder="Selecione um escritório"
                  onlyActive={true}
                  allowClear={false}
                  multiple={false}
                  disablePortal={menuZIndex != null}
                />
                {errors.escritorio_id && (
                  <FormHelperText error>{errors.escritorio_id}</FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" required error={!!errors.qtde_fo} sx={selectFormControlSx}>
                  <InputLabel>Quantidade de Fibras</InputLabel>
                  <Select
                    label="Quantidade de Fibras"
                    value={formData.qtde_fo}
                    onChange={(e) => handleInputChange('qtde_fo', Number(e.target.value))}
                    {...selectMenuProps}
                  >
                    {opcoesFo.map((fo) => (
                      <MenuItem key={fo} value={fo}>
                        {fo} FO
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.qtde_fo ? (
                    <FormHelperText>{errors.qtde_fo}</FormHelperText>
                  ) : (
                    <FormHelperText>Total de fibras ópticas no cabo</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" required error={!!errors.qtde_tubo_loose} sx={selectFormControlSx}>
                  <InputLabel>Qtde tubo loose</InputLabel>
                  <Select
                    label="Qtde tubo loose"
                    value={formData.tipo === 'asu' ? 1 : (formData.qtde_tubo_loose ?? 1)}
                    onChange={(e) => handleInputChange('qtde_tubo_loose', Number(e.target.value))}
                    disabled={formData.tipo === 'asu'}
                    {...selectMenuProps}
                  >
                    {opcoesTubosLoose.map((tubos) => (
                      <MenuItem key={tubos} value={tubos}>
                        {tubos} tubo{tubos > 1 ? 's' : ''} ({Math.floor(formData.qtde_fo / tubos)} FO/tubo)
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formData.tipo === 'asu'
                      ? 'ASU: 1 tubo loose com todas as fibras'
                      : (errors.qtde_tubo_loose || `${fibrasPorTubo} fibras por tubo • máx. 12 tubos`)}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" required error={!!errors.tipo} sx={selectFormControlSx}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    value={formData.tipo || 'as'}
                    onChange={(e) => handleInputChange('tipo', e.target.value)}
                    {...selectMenuProps}
                  >
                    {CABO_TIPOS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.tipo ? (
                    <FormHelperText>{errors.tipo}</FormHelperText>
                  ) : (
                    <FormHelperText>Drop, AS, ASU, Fig8 ou OPGW</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" required error={!!errors.padrao_tubo_loose} sx={selectFormControlSx}>
                  <InputLabel>Padrão tubo loose</InputLabel>
                  <Select
                    label="Padrão tubo loose"
                    value={formData.padrao_tubo_loose || 'abnt'}
                    onChange={(e) => handleInputChange('padrao_tubo_loose', e.target.value)}
                    disabled={formData.tipo === 'asu'}
                    {...selectMenuProps}
                  >
                    {CABO_PADROES_TUBO_LOOSE.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formData.tipo === 'asu'
                      ? 'ASU: 1 tubo loose (padrão ABNT)'
                      : (errors.padrao_tubo_loose || 'Cores das fibras dentro de cada tubo')}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" required error={!!errors.funcao_estrutural} sx={selectFormControlSx}>
                  <InputLabel>Função estrutural</InputLabel>
                  <Select
                    label="Função estrutural"
                    value={formData.funcao_estrutural || 'distribuicao'}
                    onChange={(e) => handleInputChange('funcao_estrutural', e.target.value)}
                    {...selectMenuProps}
                  >
                    {CABO_FUNCOES_ESTRUTURAIS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.funcao_estrutural ? (
                    <FormHelperText>{errors.funcao_estrutural}</FormHelperText>
                  ) : (
                    <FormHelperText>Backbone ou distribuição</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Fabricante"
                  value={formData.fabricante || ''}
                  onChange={(e) => handleInputChange('fabricante', e.target.value)}
                  placeholder="Ex: Furukawa, Corning"
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Lote"
                  value={formData.lote || ''}
                  onChange={(e) => handleInputChange('lote', e.target.value)}
                  placeholder="Número ou código do lote"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Observações"
                  value={formData.observacoes || ''}
                  onChange={(e) => handleInputChange('observacoes', e.target.value)}
                  placeholder="Informações adicionais sobre o cabo"
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <ColorLensIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Cor do Cabo
            </Typography>

            <Grid container spacing={1} sx={{ mb: 2 }}>
              {predefinedColors.map((colorOption) => (
                <Grid item key={colorOption.color}>
                  <Chip
                    label={colorOption.name}
                    onClick={() => handleInputChange('color', colorOption.color)}
                    sx={{
                      backgroundColor: formData.color === colorOption.color ? colorOption.color : 'transparent',
                      color: formData.color === colorOption.color ? '#fff' : 'inherit',
                      border: `2px solid ${colorOption.color}`,
                      '&:hover': {
                        backgroundColor: colorOption.color,
                        color: '#fff'
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <TextField
              fullWidth
              size="small"
              label="Cor Personalizada"
              value={formData.color}
              onChange={(e) => handleInputChange('color', e.target.value)}
              error={!!errors.color}
              helperText={errors.color || 'Formato: #RRGGBB'}
              placeholder="#424242"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: formData.color,
                        border: '1px solid #ccc',
                        borderRadius: 1
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <StraightenIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Pontos do Cabo
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Chip
                  icon={<TimelineIcon />}
                  label={`${formData.pontos.length} pontos`}
                  color="primary"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Chip
                  icon={<StraightenIcon />}
                  label={`~${formattedDistance}`}
                  color="secondary"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Chip
                  label={`${formData.qtde_fo} fibras`}
                  color="warning"
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {errors.pontos && (
              <FormHelperText error sx={{ mt: 1 }}>
                {errors.pontos}
              </FormHelperText>
            )}

            <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                Coordenadas dos pontos:
              </Typography>
              {formData.pontos.map((ponto: CaboPontoForm, index: number) => {
                const vinculoLabel = getPontoVinculoLabel(ponto, caixas, armarios);

                return (
                  <Box
                    key={ponto.id || index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      py: 0.25,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {index + 1}. {ponto.lat.toFixed(6)}, {ponto.lng.toFixed(6)}
                      {ponto.id && (
                        <span style={{ color: '#888', fontSize: '0.85em' }}>
                          {' '}[ID: {ponto.id}]
                        </span>
                      )}
                    </Typography>
                    {vinculoLabel && (
                      <Chip
                        size="small"
                        label={vinculoLabel}
                        color={ponto.caixa_armario_tipo === 'armario' ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          Cancelar
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Salvando...' : (cabo ? 'Atualizar' : 'Salvar Cabo')}
        </Button>
      </Box>
    </Box>
  );
};

export default CabosForm; 